import type { CentralTheme, CentralThemeMode } from "../lib/central-theme";
import {
  parseCentralThemePreference,
  resolveCentralThemeMode,
  type CentralThemePreference,
} from "../lib/central-theme-contract";

export const palette = {
  canvas: "#f5efe2",
  canvasAlt: "#eadfc9",
  ink: "#1f2b27",
  inkSoft: "#41514b",
  forest: "#29453a",
  cedar: "#6b4f3e",
  gold: "#7a5b2a",
  goldSoft: "#dec08a",
  goldDark: "#5c441f",
  mist: "#d7d2c7",
  line: "#968561",
  white: "#fbf8f1",
  success: "#55745f",
  alert: "#8e5430",
  forestDark: "#1a2e26",
};

/**
 * Central is an operational product, not a public memorial surface. Keep its
 * neutral, compact shadcn-inspired system isolated so public/family pages can
 * retain the warmer editorial palette.
 */
export const central = {
  surface: {
    canvas: "#F7F9FC",
    raised: "#ffffff",
    muted: "#EEF3F8",
    subtle: "#F7F9FC",
    sidebar: "#122F63",
    sidebarActive: "#214477",
  },
  text: {
    primary: "#14233D",
    secondary: "#2F405A",
    muted: "#4C5C73",
    inverse: "#FFFFFF",
    inverseMuted: "#DCE6F2",
  },
  border: {
    subtle: "#D6DDE8",
    default: "#C2CDDC",
    strong: "#7E8FA6",
  },
  action: {
    primary: "#122F63",
    primaryPressed: "#0B214A",
    selected: "#E8F0FA",
  },
  accent: {
    primary: "#F58220",
    soft: "#FFF1E5",
  },
  status: {
    success: { background: "#E8F4ED", foreground: "#286242" },
    warning: { background: "#FFF1E5", foreground: "#A94E0B" },
    error: { background: "#FDECEC", foreground: "#A83B3B" },
    info: { background: "#E8F0FA", foreground: "#214477" },
    neutral: { background: "#EEF3F8", foreground: "#4C5C73" },
  },
  radius: {
    control: 12,
    card: 14,
    badge: 999,
  },
  shadow: {
    shadowColor: "#14233D",
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: 0,
  },
  typography: {
    // Central is an operational product. Keep editorial serif typography on
    // family/public surfaces and use the UI family for staff-facing hierarchy.
    display: "Manrope_800ExtraBold",
    displayRegular: "Manrope_700Bold",
    body: "Manrope_400Regular",
    bodyMedium: "Manrope_500Medium",
    label: "Manrope_700Bold",
    strong: "Manrope_800ExtraBold",
  },
  layout: {
    maxWidth: 1320,
    readingWidth: 760,
    mobileActionHeight: 84,
  },
  motion: {
    feedbackDuration: 140,
    stateDuration: 220,
  },
} as const;

/**
 * Use these names in product surfaces and controls. `palette` remains available
 * for illustration and brand artwork; semantic tokens keep state and hierarchy
 * consistent when the visual system evolves.
 */
export const semantic = {
  surface: {
    canvas: palette.canvas,
    canvasAlt: palette.canvasAlt,
    raised: palette.white,
    staff: "#eef1ed",
    staffRaised: "#fbfcf9",
    inverse: palette.forest,
    subtle: "#ece8de",
    muted: "#f1eee7",
    selected: "#f7f3e9",
    accent: palette.cedar,
    dark: palette.forestDark,
    darkRaised: "#234238",
    inverseSoft: "rgba(251, 248, 241, 0.14)",
    scrim: "rgba(31, 43, 39, 0.25)",
  },
  text: {
    primary: palette.ink,
    secondary: palette.inkSoft,
    muted: palette.cedar,
    inverse: palette.white,
    inverseMuted: "rgba(251, 248, 241, 0.78)",
    placeholder: "#66706b",
    disabled: "#6d746f",
    link: palette.forest,
    onPrimary: palette.white,
  },
  border: {
    subtle: "#e1d8c9",
    default: palette.line,
    strong: palette.forest,
    staff: "#e1e7e3",
    inverse: "rgba(251, 248, 241, 0.14)",
    muted: "#d9ddd9",
    focus: palette.forest,
    error: palette.alert,
    success: palette.success,
    warning: palette.goldDark,
  },
  action: {
    primary: palette.forest,
    primaryPressed: palette.forestDark,
    destructive: palette.alert,
    secondary: palette.canvasAlt,
    focus: palette.forest,
    secondaryPressed: "#ded6c7",
    ghost: "transparent",
    disabled: "#d8d7d1",
  },
  accent: {
    foreground: palette.gold,
    soft: palette.goldSoft,
    strong: palette.goldDark,
  },
  status: {
    success: { background: "#dfe7e1", foreground: palette.forest },
    warning: { background: "#efe4d3", foreground: palette.goldDark },
    error: { background: "#f3dfd7", foreground: palette.alert },
    info: { background: "#eeece8", foreground: palette.inkSoft },
    neutral: { background: "#e4e7e2", foreground: palette.inkSoft },
  },
  privacy: {
    masked: "#e7e1d5",
    private: "#f0e7d8",
    restricted: "#eadbd2",
  },
  focus: {
    ring: palette.forest,
    ringOffset: palette.white,
    highContrast: "#111c18",
  },
  overlay: {
    dark: "rgba(0, 0, 0, 0.18)",
    darkStrong: "rgba(31, 43, 39, 0.28)",
    darkSubtle: "rgba(31, 43, 39, 0.08)",
    inverse: "rgba(251, 248, 241, 0.72)",
    inverseStrong: "rgba(251, 248, 241, 0.92)",
    inverseSubtle: "rgba(251, 248, 241, 0.14)",
    gold: "rgba(180, 134, 70, 0.18)",
    goldSubtle: "rgba(222, 192, 138, 0.16)",
    forest: "rgba(41, 69, 58, 0.12)",
    forestStrong: "rgba(41, 69, 58, 0.22)",
    forestSubtle: "rgba(41, 69, 58, 0.08)",
    ink: "rgba(31, 43, 39, 0.08)",
    line: "rgba(150, 133, 97, 0.22)",
    cedarSubtle: "rgba(107, 79, 62, 0.08)",
  },
  gradient: {
    forest: ["rgba(41, 69, 58, 0.95)", "rgba(71, 93, 80, 0.95)"] as const,
  },
  brand: {
    // Provider colors are identity artwork, not product status tokens.
    facebook: "#1877F2",
    googleSurface: "#FFFFFF",
    googleText: "#3C4043",
    googleBorder: "#DADCE0",
  },
  highContrast: {
    surface: "#ffffff",
    inverseSurface: "#111c18",
    text: "#111c18",
    inverseText: "#ffffff",
    border: "#111c18",
  },
  dark: {
    surface: "#17231f",
    raised: "#22352d",
    text: "#f5efe2",
    secondaryText: "#d7d2c7",
    border: "#7c9586",
  },
  elevation: {
    none: 0,
    panel: 5,
    sticky: 8,
    drawer: 16,
    dialog: 24,
    toast: 32,
  },
  zIndex: {
    sticky: 10,
    drawer: 20,
    dialog: 30,
    toast: 40,
    tooltip: 50,
  },
} as const;

export const spacing = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
  huge: 96,
};

export const type = {
  body: 16,
  bodySmall: 14,
  label: 14,
  caption: 12,
  title: 36,
  display: 54,
};

export const radii = {
  control: 12,
  sm: 12,
  md: 18,
  lg: 28,
  pill: 999,
};

export const shadows = {
  soft: {
    shadowColor: palette.cedar,
    shadowOpacity: 0.12,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 14 },
    elevation: semantic.elevation.panel,
  },
  none: {
    shadowColor: "transparent",
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: semantic.elevation.none,
  },
};

export const motion = {
  feedbackDuration: 120,
  stateDuration: 220,
  entranceDuration: 420,
  entranceOffset: 12,
  pressScale: 0.98,
  easing: [0.2, 0, 0, 1] as const,
  staggerDelay: 60,
  fadeInDuration: 550,
  scaleFrom: 0.95,
  reducedDuration: 0,
  reducedOffset: 0,
};

export const layout = {
  maxWidth: 1240,
  readingWidth: 760,
};

const CENTRAL_THEME_STORAGE_KEY = "wehonor-central-theme-v1";
const CENTRAL_THEME_PREFERENCE_STORAGE_KEY = "wehonor-central-theme-preference-v1";

export function applyCentralTheme(
  theme: CentralTheme,
  persist = true,
  preference = readCentralThemePreference(),
) {
  applyCentralThemeMode(
    resolveCentralThemeMode(theme, preference, systemPrefersDark()),
    theme.radius,
  );
  if (persist && typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(CENTRAL_THEME_STORAGE_KEY, JSON.stringify(theme));
    } catch {
      // Storage can be unavailable in private browsing or native runtimes.
    }
  }
}

export function setCentralThemePreference(preference: CentralThemePreference) {
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(CENTRAL_THEME_PREFERENCE_STORAGE_KEY, preference);
      const storedTheme = localStorage.getItem(CENTRAL_THEME_STORAGE_KEY);
      if (storedTheme) {
        const theme = JSON.parse(storedTheme) as unknown;
        if (isCentralTheme(theme)) applyCentralTheme(theme, false, preference);
      }
    } catch {
      // Storage can be unavailable in private browsing.
    }
  }
}

export function readCentralThemePreference(): CentralThemePreference {
  if (typeof localStorage === "undefined") return "system";
  try {
    return parseCentralThemePreference(
      localStorage.getItem(CENTRAL_THEME_PREFERENCE_STORAGE_KEY),
    );
  } catch {
    return "system";
  }
}

export function hydrateStoredCentralTheme() {
  if (typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem(CENTRAL_THEME_STORAGE_KEY);
    if (!raw) return;
    const candidate = JSON.parse(raw) as unknown;
    if (isCentralTheme(candidate)) applyCentralTheme(candidate, false);
  } catch {
    // A malformed local theme should never prevent Central from booting.
  }
}

function isCentralTheme(value: unknown): value is CentralTheme {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<CentralTheme>;
  return candidate.version === 1
    && (candidate.radius === "none" || candidate.radius === "sm" || candidate.radius === "md" || candidate.radius === "lg")
    && isThemeMode(candidate.modes?.light)
    && isThemeMode(candidate.modes?.dark);
}

function isThemeMode(value: unknown): value is CentralThemeMode {
  if (typeof value !== "object" || value === null) return false;
  const mode = value as Partial<CentralThemeMode>;
  return [
    "background", "foreground", "card", "cardForeground", "primary", "primaryForeground",
    "secondary", "secondaryForeground", "muted", "mutedForeground", "accent", "accentForeground",
    "border", "input", "ring",
  ].every((key) => {
    const color = mode[key as keyof CentralThemeMode];
    return typeof color === "string" && (/^#[0-9a-f]{6}$/i.test(color) || /^hsl\(/i.test(color));
  });
}

function applyCentralThemeMode(mode: CentralThemeMode, themeRadius: CentralTheme["radius"]) {
  Object.assign(central.surface, {
    canvas: mode.background,
    raised: mode.card,
    muted: mode.muted,
    subtle: mode.muted,
    sidebar: mode.primary,
    sidebarActive: mode.secondary,
  });
  Object.assign(central.text, {
    primary: mode.foreground,
    secondary: mode.mutedForeground,
    muted: mode.mutedForeground,
    inverse: mode.primaryForeground,
    inverseMuted: mode.secondaryForeground,
  });
  Object.assign(central.border, {
    subtle: mode.border,
    default: mode.input,
    strong: mode.ring,
  });
  Object.assign(central.action, {
    primary: mode.primary,
    primaryPressed: mode.accent,
    selected: mode.secondary,
  });
  const radius = { none: 0, sm: 4, md: 8, lg: 14 }[themeRadius];
  Object.assign(central.radius, {
    control: radius,
    card: radius,
    badge: Math.max(4, radius - 2),
  });
  Object.assign(semantic.surface, {
    staff: mode.background,
    staffRaised: mode.card,
    subtle: mode.muted,
    muted: mode.muted,
    selected: mode.secondary,
  });
  Object.assign(semantic.text, {
    primary: mode.foreground,
    secondary: mode.mutedForeground,
    muted: mode.mutedForeground,
    placeholder: mode.mutedForeground,
    link: mode.primary,
    onPrimary: mode.primaryForeground,
  });
  Object.assign(semantic.border, {
    subtle: mode.border,
    default: mode.input,
    strong: mode.ring,
    staff: mode.border,
    focus: mode.ring,
  });
  Object.assign(semantic.action, {
    primary: mode.primary,
    primaryPressed: mode.accent,
    secondary: mode.secondary,
    secondaryPressed: mode.accent,
    focus: mode.ring,
  });
}

function systemPrefersDark() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;
}

if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  const colorScheme = window.matchMedia("(prefers-color-scheme: dark)");
  colorScheme.addEventListener?.("change", () => {
    if (readCentralThemePreference() !== "system") return;
    hydrateStoredCentralTheme();
  });
}

hydrateStoredCentralTheme();
